import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface LocationAccordionFilterProps {
  /** Ordered district names, each with at least one city (last entry is often "Outros"). */
  districts: string[];
  /** District -> its cities. */
  citiesByDistrict: Record<string, string[]>;
  /** Currently selected cities (flat, matches FilterState.location). */
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  clearAllText?: string;
  onOpenChange?: (isOpen: boolean) => void;
  "data-testid"?: string;
}

type DistrictCheckState = "none" | "some" | "all";

// District -> city accordion filter: checking a district selects all of its
// cities in one action; individual cities can then be unchecked, which flips
// the district row to an indeterminate state. Selection is always a flat
// array of cities — districts are purely a grouping/UX affordance, never a
// separate value sent anywhere.
const LocationAccordionFilter: React.FC<LocationAccordionFilterProps> = ({
  districts,
  citiesByDistrict,
  value,
  onChange,
  placeholder: placeholderProp,
  label,
  disabled = false,
  clearAllText: clearAllTextProp,
  onOpenChange,
  "data-testid": dataTestId,
}) => {
  const { t } = useTranslation("common");
  const placeholder = placeholderProp ?? t("searchableDropdown.placeholder");
  const clearAllText = clearAllTextProp ?? t("locationFilter.clearAll");
  const [isOpen, setIsOpen] = useState(false);
  const [expandedDistricts, setExpandedDistricts] = useState<Set<string>>(
    new Set(),
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
    openUpwards: boolean;
  } | null>(null);

  const updateMenuPosition = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const estimatedHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpwards = spaceBelow < estimatedHeight && rect.top > spaceBelow;
    setMenuPos({
      top: openUpwards ? rect.top : rect.bottom,
      left: rect.left,
      width: rect.width,
      openUpwards,
    });
  }, []);

  useEffect(() => {
    const isOutside = (target: Node) =>
      Boolean(
        containerRef.current &&
          !containerRef.current.contains(target) &&
          (!menuRef.current || !menuRef.current.contains(target)),
      );

    const close = () => {
      setIsOpen(false);
      onOpenChange?.(false);
    };

    const handleMouseDown = (event: MouseEvent) => {
      if (isOutside(event.target as Node)) close();
    };

    let touchMoved = false;
    const handleTouchStart = () => {
      touchMoved = false;
    };
    const handleTouchMove = () => {
      touchMoved = true;
    };
    const handleTouchEnd = (event: TouchEvent) => {
      if (touchMoved) return;
      if (isOutside(event.target as Node)) close();
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleMouseDown);
      document.addEventListener("touchstart", handleTouchStart);
      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);
    }

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };
    if (isOpen) document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onOpenChange]);

  const districtCheckState = (district: string): DistrictCheckState => {
    const cities = citiesByDistrict[district] ?? [];
    if (cities.length === 0) return "none";
    const selectedCount = cities.filter((city) => value.includes(city)).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === cities.length) return "all";
    return "some";
  };

  const toggleDistrict = (district: string) => {
    const cities = citiesByDistrict[district] ?? [];
    const state = districtCheckState(district);
    if (state === "all") {
      // Fully selected -> deselect every city in this district.
      onChange(value.filter((city) => !cities.includes(city)));
    } else {
      // None or partially selected -> select every city in this district.
      const withoutDistrict = value.filter((city) => !cities.includes(city));
      onChange([...withoutDistrict, ...cities]);
    }
  };

  const toggleCity = (city: string) => {
    if (value.includes(city)) {
      onChange(value.filter((c) => c !== city));
    } else {
      onChange([...value, city]);
    }
  };

  const toggleExpanded = (district: string) => {
    setExpandedDistricts((prev) => {
      const next = new Set(prev);
      if (next.has(district)) next.delete(district);
      else next.add(district);
      return next;
    });
  };

  const clearAll = () => onChange([]);

  const handleContainerClick = () => {
    if (disabled) return;
    const next = !isOpen;
    setIsOpen(next);
    onOpenChange?.(next);
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) return value[0];
    return t("locationFilter.selected", { count: value.length });
  };

  const menuBody = (
    <div className="overflow-y-auto flex-1">
      {districts.length === 0 ? (
        <div className="px-4 py-3 text-sm text-gray-500 text-center">
          {t("animalFilters.noOptions")}
        </div>
      ) : (
        districts.map((district) => {
          const cities = citiesByDistrict[district] ?? [];
          const state = districtCheckState(district);
          const expanded = expandedDistricts.has(district);
          return (
            <div key={district} className="border-b border-gray-100">
              <div className="w-full flex items-center gap-2 px-3 py-2 hover:bg-tabby-light">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(district);
                  }}
                  className="p-0.5 text-gray-400 hover:text-charcoal"
                  aria-label={expanded ? t("actions.close") : t("locationFilter.expand")}
                >
                  {expanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleDistrict(district);
                  }}
                  className="flex-1 flex items-center gap-3 text-left"
                >
                  <span
                    className={`flex items-center justify-center w-4 h-4 rounded-sm border-2 transition-colors duration-150 flex-shrink-0 ${
                      state === "all"
                        ? "border-main-purple bg-main-purple"
                        : state === "some"
                          ? "border-main-purple bg-white"
                          : "border-gray-400 bg-white"
                    }`}
                  >
                    {state === "all" && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path
                          d="M10 3L4.5 8.5L2 6"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    {state === "some" && (
                      <span className="w-2 h-0.5 bg-main-purple rounded-full" />
                    )}
                  </span>
                  <span className="text-charcoal font-medium">{district}</span>
                  <span className="ml-auto text-xs text-gray-400 font-medium">
                    {cities.length}
                  </span>
                </button>
              </div>

              {expanded && (
                <div className="pb-1">
                  {cities.map((city) => {
                    const isSelected = value.includes(city);
                    return (
                      <button
                        key={city}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCity(city);
                        }}
                        className="w-full pl-11 pr-4 py-1.5 text-left transition-colors duration-200 text-sm flex items-center gap-3 hover:bg-tabby-light"
                      >
                        <span
                          className={`flex items-center justify-center w-3.5 h-3.5 rounded-sm border-2 transition-colors duration-150 flex-shrink-0 ${
                            isSelected
                              ? "border-main-purple bg-main-purple"
                              : "border-gray-400 bg-white"
                          }`}
                        >
                          {isSelected && (
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                              <path
                                d="M10 3L4.5 8.5L2 6"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </span>
                        <span className="text-charcoal">{city}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })
      )}
      {value.length > 0 && (
        <div className="border-t-2 border-gray-200 bg-white sticky bottom-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            className="w-full px-4 py-3 text-sm text-main-purple hover:bg-tabby-light transition-colors duration-200 font-semibold text-center"
          >
            {clearAllText}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div ref={containerRef} className="relative min-w-0" data-testid={dataTestId}>
      {label && (
        <label className="block text-xs sm:text-sm font-medium text-my-black mb-1">
          {label}
        </label>
      )}

      <div
        onClick={handleContainerClick}
        className={`
          relative w-full px-3 py-3 pr-10 rounded-lg bg-white appearance-none cursor-pointer
          transition-all duration-200 text-[0.95rem] sm:text-sm font-medium shadow-sm flex items-center
          ${
            value.length > 0
              ? "border-2 border-main-purple text-charcoal hover:border-gradient-blue focus-within:border-gradient-blue focus-within:ring-2 focus-within:ring-main-purple/20"
              : "border-2 border-gray-300 text-gray-500 hover:border-main-purple focus-within:border-main-purple focus-within:ring-2 focus-within:ring-gray-300/20"
          }
          ${disabled ? "opacity-50 cursor-not-allowed" : ""}
          ${isOpen ? "ring-2 ring-main-purple/20" : ""}
        `}
      >
        <div className="flex-1 flex items-center gap-2 flex-wrap whitespace-nowrap overflow-hidden">
          <span className={value.length > 0 ? "text-charcoal font-medium truncate" : "text-gray-500"}>
            {getDisplayText()}
          </span>
        </div>

        {value.length > 0 && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearAll();
            }}
            className="mr-2 p-1 hover:bg-gray-100 rounded-full transition-colors z-10"
            title={clearAllText}
          >
            <X className="w-4 h-4 text-gray-500 hover:text-charcoal" />
          </button>
        )}

        <ChevronDown
          className={`w-4 h-4 pointer-events-none transition-all duration-200 ${
            value.length > 0 ? "text-main-purple" : "text-gray-400"
          } ${isOpen ? "rotate-180" : ""}`}
        />
      </div>

      {isOpen &&
        !disabled &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              transform: menuPos.openUpwards
                ? "translateY(calc(-100% - 4px))"
                : "translateY(4px)",
              zIndex: 10050,
            }}
            className="bg-white border-2 border-main-purple rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col"
          >
            {menuBody}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default LocationAccordionFilter;
